import React, { useEffect, useState, useMemo } from "react";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Eye, User, Check, X as XIcon, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { PhotoUploader } from "@/components/PhotoUploader";
import { useConfirm } from "@/components/ConfirmDialog";
import { whatsappLink } from "@/lib/whatsapp";

const CATEGORIES = ["Sub-12", "Sub-14", "Sub-16", "Sub-20", "Adulto"];
const POSITIONS = ["Levantador(a)", "Oposto(a)", "Central", "Ponteiro(a)", "Líbero", "Não definida"];

const emptyForm = {
  first_name: "", last_name: "", birth_date: "", cpf: "",
  position: "", phone: "", guardian: "", guardian_phone: "",
  class_id: "", status: "ativo", email: "", password: "",
  photo_url: null, monthly_fee: "80", gender: "",
};

export default function Students() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState({});
  const [query, setQuery] = useState("");
  const [filterCat, setFilterCat] = useState("__all__");
  const [filterStatus, setFilterStatus] = useState("__all__");
  const [filterClass, setFilterClass] = useState("__all__");
  const [filterPos, setFilterPos] = useState("__all__");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    try {
      const [s, c, st] = await Promise.all([
        api.get("/students"),
        api.get("/classes"),
        api.get("/admin/students-stats"),
      ]);
      setStudents(s.data);
      setClasses(c.data);
      setStats(st.data || {});
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const pendingCount = useMemo(() => students.filter(s => s.status === "pendente").length, [students]);

  const filtered = useMemo(() => {
    return students.filter((s) => {
      const q = query.toLowerCase();
      const matchQ = !q || s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q);
      const matchC = filterCat === "__all__" || s.category === filterCat;
      const matchS = filterStatus === "__all__" || s.status === filterStatus;
      const matchT = filterClass === "__all__"
        || (filterClass === "__none__" ? !s.class_id : s.class_id === filterClass);
      const matchP = filterPos === "__all__"
        || (filterPos === "__none__" ? !s.position : s.position === filterPos);
      return matchQ && matchC && matchS && matchT && matchP;
    }).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [students, query, filterCat, filterStatus, filterClass, filterPos]);

  const startCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const startEdit = (s) => {
    setEditing(s);
    setForm({
      first_name: s.first_name || (s.name?.split(" ")[0] || ""),
      last_name: s.last_name || (s.name?.split(" ").slice(1).join(" ") || ""),
      birth_date: s.birth_date, cpf: s.cpf || "",
      position: s.position || "", phone: s.phone || "",
      gender: s.gender || "",
      guardian: s.guardian || "", guardian_phone: s.guardian_phone || "",
      class_id: s.class_id || "", status: s.status,
      email: s.email, password: "",
      photo_url: s.photo_url || null,
      monthly_fee: s.monthly_fee != null ? String(s.monthly_fee) : "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.class_id === "" || payload.class_id === "__none__") payload.class_id = null;
      if (payload.monthly_fee === "" || payload.monthly_fee == null) payload.monthly_fee = null;
      else payload.monthly_fee = Number(payload.monthly_fee);
      if (editing) {
        if (!payload.password) delete payload.password;
        await api.put(`/students/${editing.id}`, payload);
        toast.success("Aluno atualizado");
      } else {
        await api.post("/students", payload);
        toast.success("Aluno cadastrado");
      }
      setOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const approve = async (s) => {
    try {
      await api.post(`/students/${s.id}/approve`);
      toast.success(`${s.name} aprovado(a)`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (s) => {
    const ok = await confirm({
      title: `Excluir ${s.name}?`,
      description: "Esta ação remove o aluno e todos os dados associados (presenças, avaliações e financeiro). Não pode ser desfeita.",
      confirmText: "Excluir", destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/students/${s.id}`); toast.success("Aluno removido"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const className = (id) => classes.find((c) => c.id === id)?.name || "—";

  const effectiveStatusChip = (s) => {
    const st = stats[s.id];
    if (s.status === "ativo" && st?.has_overdue) {
      return <span className="vv-chip vv-chip-red whitespace-nowrap font-bold" title="Aluno com mensalidade em atraso">ATRASADO</span>;
    }
    const map = { ativo: "vv-chip-lime", inativo: "vv-chip-muted", pendente: "vv-chip-yellow" };
    return <span className={`vv-chip ${map[s.status] || "vv-chip-muted"} whitespace-nowrap`}>{s.status}</span>;
  };

  const overallBadge = (s) => {
    const o = stats[s.id]?.overall;
    if (o == null) return <span className="text-xs text-muted-foreground">—</span>;
    const color = o > 100 ? "text-[hsl(45_100%_60%)]"
      : o > 80 ? "text-[hsl(210_12%_82%)]"
      : o > 60 ? "text-[hsl(25_75%_60%)]"
      : "text-[hsl(var(--vv-blue))]";
    return <span className={`font-black text-lg ${color}`} data-testid={`overall-${s.id}`}>{o}</span>;
  };

  const attendanceBadge = (s) => {
    const p = stats[s.id]?.attendance_pct ?? 0;
    const color = p >= 75 ? "text-[hsl(var(--vv-lime))]" : p >= 50 ? "text-[hsl(var(--vv-yellow))]" : "text-muted-foreground";
    return <span className={`font-black ${color}`} data-testid={`attendance-${s.id}`}>{p}%</span>;
  };

  const openStudentWa = (s) => {
    const phone = s.phone;
    if (!phone) { toast.error("Aluno sem telefone cadastrado"); return; }
    const url = whatsappLink(phone, `Olá, ${s.first_name || s.name}! Aqui é da escola Vôlei Vida.`);
    if (!url) { toast.error("Telefone inválido"); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="admin-students-page">
      <PageHeader
        eyebrow="Cadastro" title="Alunos"
        subtitle="Cadastre, edite e acompanhe todos os atletas da escola."
        mascot={BRAND.MANGUITA_COACH}
        actions={
          <Button onClick={startCreate} data-testid="new-student-button" className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] hover:bg-[hsl(var(--vv-lime)/0.85)] font-bold uppercase tracking-wider">
            <Plus className="size-4 mr-2" /> Novo aluno
          </Button>
        }
      />

      {pendingCount > 0 && (
        <div className="vv-card p-3 mb-4 flex items-center gap-3 border-[hsl(var(--vv-yellow))/0.4]" data-testid="pending-banner">
          <div className="size-10 rounded-full bg-[hsl(var(--vv-yellow))/0.18] flex items-center justify-center text-[hsl(var(--vv-yellow))] font-black">
            {pendingCount}
          </div>
          <div className="flex-1">
            <div className="font-bold">Aluno(s) aguardando aprovação</div>
            <div className="text-xs text-muted-foreground">Filtre por status "pendente" para revisar.</div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setFilterStatus("pendente")}>Ver pendentes</Button>
        </div>
      )}

      <div className="vv-card p-3 mb-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou email..."
            value={query} onChange={(e) => setQuery(e.target.value)}
            className="pl-9 bg-background/60"
            data-testid="students-search-input"
          />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="sm:w-44 bg-background/60" data-testid="students-filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas categorias</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="sm:w-40 bg-background/60" data-testid="students-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos status</SelectItem>
            <SelectItem value="ativo">Ativos</SelectItem>
            <SelectItem value="inativo">Inativos</SelectItem>
            <SelectItem value="pendente">Pendentes</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterClass} onValueChange={setFilterClass}>
          <SelectTrigger className="sm:w-48 bg-background/60" data-testid="students-filter-class"><SelectValue placeholder="Turma" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas as turmas</SelectItem>
            <SelectItem value="__none__">Sem turma</SelectItem>
            {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterPos} onValueChange={setFilterPos}>
          <SelectTrigger className="sm:w-44 bg-background/60" data-testid="students-filter-position"><SelectValue placeholder="Posição" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas as posições</SelectItem>
            <SelectItem value="__none__">Sem posição</SelectItem>
            {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Nenhum aluno encontrado" description="Comece cadastrando seu primeiro aluno." mascot={BRAND.MANGUITO} action={<Button onClick={startCreate}>Cadastrar aluno</Button>} />
      ) : (
        <>
          {/* Mobile cards */}
          <div className="sm:hidden space-y-2">
            {filtered.map((s) => (
              <div key={s.id} className="vv-card p-3" data-testid={`student-card-${s.id}`}>
                <div className="flex items-center gap-3">
                  {s.photo_url ? (
                    <img src={s.photo_url} alt="" className="size-12 rounded-full object-cover border-2 border-[hsl(var(--vv-lime))/0.4] shrink-0" />
                  ) : (
                    <div className="size-12 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <User className="size-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{s.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{s.position || "Sem posição"}</div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0">
                    {overallBadge(s)}
                    <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Overall</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="vv-chip vv-chip-blue whitespace-nowrap">{s.category}</span>
                  {effectiveStatusChip(s)}
                  <span className="text-xs text-muted-foreground ml-auto">Freq. {attendanceBadge(s)}</span>
                </div>
                <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border">
                  {s.status === "pendente" && (
                    <Button size="sm" variant="outline" className="border-[hsl(var(--vv-lime))/0.5] text-[hsl(var(--vv-lime))]" onClick={() => approve(s)} data-testid={`approve-student-${s.id}`}>
                      <Check className="size-3 mr-1" /> Aprovar
                    </Button>
                  )}
                  {s.phone && (
                    <Button size="sm" variant="ghost" onClick={() => openStudentWa(s)} data-testid={`whatsapp-student-${s.id}`} title="Falar no WhatsApp" className="text-[#25D366]">
                      <MessageCircle className="size-4" />
                    </Button>
                  )}
                  <Button asChild size="sm" variant="ghost"><Link to={`/admin/alunos/${s.id}`}><Eye className="size-4" /></Link></Button>
                  <Button size="sm" variant="ghost" onClick={() => startEdit(s)}><Pencil className="size-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-destructive ml-auto" onClick={() => del(s)}><Trash2 className="size-4" /></Button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block vv-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="uppercase text-xs">
                  <TableHead>Nome</TableHead>
                  <TableHead className="whitespace-nowrap">Categoria</TableHead>
                  <TableHead>Posição</TableHead>
                  <TableHead>Turma</TableHead>
                  <TableHead className="text-center">Overall</TableHead>
                  <TableHead className="text-center">Freq.</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {s.photo_url ? (
                          <img src={s.photo_url} alt="" className="size-10 rounded-full object-cover border-2 border-[hsl(var(--vv-lime))/0.4]" />
                        ) : (
                          <div className="size-10 rounded-full bg-muted flex items-center justify-center"><User className="size-5 text-muted-foreground" /></div>
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold whitespace-nowrap truncate max-w-[200px]">{s.name}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">{s.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><span className="vv-chip vv-chip-blue whitespace-nowrap">{s.category}</span></TableCell>
                    <TableCell className="whitespace-nowrap">{s.position || "—"}</TableCell>
                    <TableCell className="whitespace-nowrap">{className(s.class_id)}</TableCell>
                    <TableCell className="text-center">{overallBadge(s)}</TableCell>
                    <TableCell className="text-center">{attendanceBadge(s)}</TableCell>
                    <TableCell>{effectiveStatusChip(s)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {s.status === "pendente" && (
                          <Button size="sm" variant="outline" className="border-[hsl(var(--vv-lime))/0.5] text-[hsl(var(--vv-lime))] mr-1" onClick={() => approve(s)} data-testid={`approve-student-${s.id}`}>
                            <Check className="size-3 mr-1" /> Aprovar
                          </Button>
                        )}
                        {s.phone && (
                          <Button size="icon" variant="ghost" onClick={() => openStudentWa(s)} data-testid={`whatsapp-student-${s.id}`} title="Falar com o aluno no WhatsApp" className="text-[#25D366]">
                            <MessageCircle className="size-4" />
                          </Button>
                        )}
                        <Button asChild size="icon" variant="ghost"><Link to={`/admin/alunos/${s.id}`}><Eye className="size-4" /></Link></Button>
                        <Button size="icon" variant="ghost" onClick={() => startEdit(s)}><Pencil className="size-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(s)}><Trash2 className="size-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="student-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar aluno" : "Novo aluno"}</DialogTitle>
            <DialogDescription>Preencha os dados. Email e senha serão usados para login do aluno.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="mb-2 block">Foto do aluno</Label>
              <PhotoUploader value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} />
            </div>
            <div>
              <Label>Nome *</Label>
              <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="student-firstname-input" />
            </div>
            <div>
              <Label>Sobrenome</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="student-lastname-input" />
            </div>
            <div>
              <Label>Data de nascimento *</Label>
              <Input type="date" required value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="student-birthdate-input" />
            </div>
            <div>
              <Label>CPF</Label>
              <Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" data-testid="student-cpf-input" />
            </div>
            <div>
              <Label>Posição</Label>
              <Select value={form.position || "__none__"} onValueChange={(v) => setForm({ ...form, position: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Gênero</Label>
              <Select value={form.gender || "__none__"} onValueChange={(v) => setForm({ ...form, gender: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="student-gender-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  <SelectItem value="F">Feminino</SelectItem>
                  <SelectItem value="M">Masculino</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Turma</Label>
              <Select value={form.class_id || "__none__"} onValueChange={(v) => setForm({ ...form, class_id: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Sem turma" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem turma</SelectItem>
                  {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mensalidade fixa (R$)</Label>
              <Input
                type="number" min={0} step="0.01"
                value={form.monthly_fee}
                onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })}
                placeholder="Ex: 80.00"
                data-testid="student-monthly-fee-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Padrão R$ 80,00 — edite se necessário.</p>
            </div>
            <div>
              <Label>Telefone do aluno</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label>Responsável</Label>
              <Input value={form.guardian} onChange={(e) => setForm({ ...form, guardian: e.target.value })} />
            </div>
            <div>
              <Label>Telefone do responsável</Label>
              <Input value={form.guardian_phone} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} data-testid="student-guardian-phone-input" />
            </div>
            <div>
              <Label>Email de login *</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="student-email-input" />
            </div>
            <div>
              <Label>{editing ? "Nova senha (opcional)" : "Senha *"}</Label>
              <Input
                type="password"
                required={!editing}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? "Deixe em branco para manter" : ""}
                data-testid="student-password-input"
              />
            </div>
            <DialogFooter className="sm:col-span-2 sticky bottom-0 bg-card pt-3 -mx-6 px-6 border-t border-border">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-student-button" className="bg-[hsl(var(--vv-blue))] font-bold uppercase">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
